import { Navigate, useLocation } from 'react-router-dom'
import { navSubjectFrom } from '../navSubject'
import { useCan, useSession } from '../useSession'
import { ParentSchedulePanel } from './PortalView'
import { TeacherSchedule } from './TeacherPortalView'

/**
 * Her own days — both halves of them, at one URL (D-322, D-326).
 *
 * **This is the screen the portal switch existed to choose between**, and the
 * reason it no longer has to. A teacher who is also a parent used to pick a
 * portal and get one answer: her classes *or* her children's. Sixteen people
 * here are both and two are linked in production today, and the question they
 * actually ask on a Tuesday spans both — *where does everyone need to be.*
 *
 * **One half at a time when both apply, each with its own address** (D-352).
 * They were stacked with headings until the stacking was measured: 2.6 screens
 * on a phone to reach the second one. The view bar names them now, so the
 * headings went with the stacking — a chip highlighted *My classes* directly
 * above a heading reading "What you are teaching" says one thing twice.
 *
 * **Everyone with a single relationship sees exactly what she saw before**: her
 * one half, at `/schedule`, with no bar and no second URL. The split is a thing
 * that happens to the sixteen who are both, and to nobody else.
 *
 * Reached only by an account that has one — `RequireDestination` gates on the
 * same `teaches || isParent` the rail uses, so Tonya, who is staff and neither,
 * gets Calendar instead of an empty week (D-322).
 */
export default function ScheduleScreen() {
  const { pathname, search } = useLocation()
  const { session } = useSession()

  /*
   * Read before the guard below, because a hook cannot be skipped on the
   * render where there is no session yet.
   */
  const mayReport = useCan('ArrangeAttendance')
  const mayBook = useCan('BookPrivates')

  if (!session) return null

  const subject = navSubjectFrom(session)

  /**
   * <b>A dancer holding either switch gets the acting half; one holding
   * neither keeps the calendar</b> (D-926, amending D-539). Both controls a
   * parent can hand over (D-920) live in <c>ParentSchedulePanel</c> and
   * nowhere else.
   *
   * ⚠ <c>isDancer</c> is asked as well because both switches are in the Admin
   * set too, so the capability alone would move the owner onto a children's
   * half with no children in it.
   */
  const acts = subject.isDancer && (mayReport || mayBook)
  const dancerHalf = subject.isParent || acts

  const both = subject.teaches && subject.isParent

  /*
   * **Both halves at one URL was the right answer while they fitted** (D-326).
   * Measured on a real dual-role account they do not: her children's heading
   * sat 2,184px down a 390px phone, and the week above it is the same 1,472px
   * whether it holds twelve classes or none. So for the sixteen people who are
   * both, the halves become separately addressable and the view bar names them
   * — which is D-326's own sentence, not a return to D-309's portal switch.
   * **Nothing here remembers which she was on**: the address says it, every
   * time (D-352).
   *
   * For everybody else this is exactly as it was — one relationship, one
   * screen, no bar, no second URL.
   */
  /*
   * **Which half is being asked for** — by the address, or by a query only the
   * parent half can read. A parent's search hit arrives as
   * `/schedule?dancer=<id>` (D-329).
   *
   * **`dancer` alone, never `week` or `view`** (D-360). Those two used to be
   * here, on the reasoning that `ParentSchedulePanel` was the only reader of
   * them — and the moment the teaching half started keeping its anchor in the
   * address too, that test sent a teacher on `/schedule/classes?week=2026-10-05`
   * straight to her children's tab. It won over an explicit path, so the more
   * specific address lost to the vaguer signal.
   *
   * `dancer` survives because it still names something only one half has. The
   * other two now mean the same thing on both, which is what lets the date
   * carry across the tabs at all.
   */
  const params = new URLSearchParams(search)
  const asksForParentHalf = params.has('dancer')

  /*
   * **The path first, and the query only when the path is silent** (D-360).
   *
   * The query used to be tested before the path was consulted at all, so a
   * parameter beat an address that named the half in as many words. That was
   * survivable while `?dancer=` was the only carried parameter and only ever
   * arrived on the plain `/schedule`; it stops being survivable the moment the
   * view bar carries anything, because the more specific statement loses to the
   * vaguer one and she is bounced off the half she just chose.
   */
  const asked = pathname.startsWith('/schedule/dancers')
    ? 'dancers'
    : pathname.startsWith('/schedule/classes')
      ? 'classes'
      : asksForParentHalf
        ? 'dancers'
        : null

  /*
   * **What she can actually be shown**: what she asked for if she holds it,
   * otherwise the one relationship she has. Decided from the reader rather
   * than from the path, because `DestinationScreen` titles the screen from
   * whichever view matches the URL and gates on nothing — so a teacher who is
   * nobody's parent reaching `/schedule/dancers` got an `<h1>My children</h1>`
   * over her own teaching week, with no bar on screen to explain it.
   */
  /*
   * ⚠ **The first branch asks `isParent`, not `dancerHalf`, on purpose**
   * (D-926). Only an account with *both* halves has a named address to ask
   * for — and the one reader `dancerHalf` would add here is the granted
   * student teacher, who has no bar offering it and lands on her teaching
   * half from every other entrance. Honouring it for her at `?dancer=` alone
   * would be one hand-typed URL behaving differently from the whole rest of
   * the app, and would make `docs/GAPS.md` wrong where it says she cannot
   * reach it. An audit found this branch doing exactly that.
   */
  const target = asked === 'dancers' && subject.isParent
    ? 'dancers'
    : asked === 'classes' && subject.teaches
      ? 'classes'
      : subject.teaches
        ? 'classes'
        : dancerHalf
          ? 'dancers'
          : /*
             * **A dancer signed in as herself gets the week view** (D-539),
             * unless a parent has handed her something to do — see `acts`
             * above (D-926). This is the reader, and nothing more.
             *
             * `classes` is the wrong word for her and the right component: it
             * is the calendar, and with `teachingOnly` off it is exactly her
             * own classes. Her account holds `ViewSchedule` and nothing else,
             * and the controls that read a capability — declare out, book a
             * room, take a register — gate themselves off. **Two did not**,
             * and an audit found them: every class card offered a roster the
             * server refuses, and the day grid's empty slots invited her to
             * book a room. Both are gated by hand now (`canOpenRoster`, the
             * day view's `canBook`); an earlier draft of this comment claimed
             * "every" and was wrong.
             *
             * Without this she resolved to `null` and was redirected to a
             * screen that rendered nothing: an account, a password, and an
             * empty app. Most dancers are not student teachers, so that was
             * the *ordinary* outcome of this feature rather than an edge.
             */
            subject.isDancer
            ? 'classes'
            : null

  /*
   * Nothing to show her — Tonya, who is staff and neither. Still sent to the
   * plain address rather than left on a sub-path, because the heading is drawn
   * from the path and would name a half she does not have. `RequireDestination`
   * refuses this reader first, so nothing reaches here today; the point is that
   * the guard against a wrong heading should not live in another file.
   */
  if (!target) return pathname === '/schedule' ? null : <Navigate to="/schedule" replace />

  /*
   * **The address that names it.** Only an account with both halves has named
   * addresses; for everybody else the plain one is theirs, and a sub-path would
   * put a heading naming a half she does not have over her week. Search is
   * preserved either way — see above.
   */
  const belongs = both ? `/schedule/${target}` : '/schedule'
  if (pathname !== belongs) return <Navigate to={{ pathname: belongs, search }} replace />

  const showTeaching = target === 'classes'
  const showDancers = target === 'dancers'

  return (
    <>
      {/*
        **`CoverPanel` was here and is gone** (D-365).

        Owner: *"burying too much stuff with scrolling on the schedule page ...
        they wouldn't see it."* Its three buckets went three different ways —
        what is asked of her to home, answerable in place; what she declared and
        has not resolved into the day dialog, reached from a line on home; and
        what she is covering onto her week, beside the class, where a substitute
        badge now says so.

        This screen is a week again, which is what a teacher opens it for.
      */}
      {showTeaching && (
        <section>
          <TeacherSchedule />
        </section>
      )}

      {showDancers && (
        <section>
          <ParentSchedulePanel />
        </section>
      )}
    </>
  )
}
